import React from "react";
import styled, { css } from "styled-components";
import { tokens } from "../../theme/tokens";

// StatCard / KpiCard — label + value + optional delta + icon. For dashboards.
//
//   <StatCard label="Active riders" value="1,204" delta={+12} icon={<Icon/>} />

const { colors, radii, shadows, spacing, typography, motion } = tokens;

const Root = styled.div`
    position: relative;
    overflow: hidden;
    background: ${colors.surface};
    border: 1px solid ${colors.border};
    border-radius: ${radii.lg};
    box-shadow: ${shadows.sm};
    padding: ${spacing[5]};
    transition: box-shadow ${motion.base} ${motion.ease}, transform ${motion.base} ${motion.ease};

    ${(p) =>
        p.$accent &&
        css`
            &::before {
                content: "";
                position: absolute;
                inset: 0 auto 0 0;
                width: 4px;
                background: linear-gradient(${colors.vermilion}, ${colors.crimson});
            }
        `};

    &:hover { box-shadow: ${shadows.md}; transform: translateY(-2px); }
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing[3]};
`;

const Label = styled.span`
    font-size: ${typography.size.sm};
    font-weight: ${typography.weight.semibold};
    letter-spacing: ${typography.tracking.wide};
    text-transform: uppercase;
    color: ${colors.muted};
`;

const IconWrap = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: ${radii.md};
    background: ${colors.rose};
    color: ${colors.primary};
    flex-shrink: 0;
    svg { width: 20px; height: 20px; }
`;

const Value = styled.div`
    margin-top: ${spacing[3]};
    font-family: ${typography.fonts.body};
    font-size: ${typography.size["3xl"]};
    font-weight: ${typography.weight.extrabold};
    line-height: 1.05;
    letter-spacing: ${typography.tracking.tight};
    color: ${colors.heading};
`;

const Delta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: ${spacing[2]};
    font-size: ${typography.size.sm};
    font-weight: ${typography.weight.semibold};
    color: ${(p) => (p.$dir < 0 ? colors.status.danger.fg : colors.status.success.fg)};
    svg { width: 12px; height: 12px; }
`;

const SubText = styled.span`
    margin-left: 6px;
    color: ${colors.muted};
    font-weight: ${typography.weight.regular};
`;

const Hint = styled.div`
    margin-top: ${spacing[2]};
    font-size: ${typography.size.sm};
    color: ${colors.muted};
    line-height: ${typography.leading.snug};
`;

export default function StatCard({ label, value, delta, deltaLabel, hint, icon, accent = true, ...rest }) {
    const dir = typeof delta === "number" ? Math.sign(delta) : 0;
    return (
        <Root $accent={accent} {...rest}>
            <Head>
                <Label>{label}</Label>
                {icon && <IconWrap>{icon}</IconWrap>}
            </Head>
            <Value>{value}</Value>
            {hint != null && <Hint>{hint}</Hint>}
            {delta != null && (
                <Delta $dir={dir}>
                    <svg viewBox="0 0 12 12" fill="none">
                        <path
                            d={dir < 0 ? "M3 5l3 3 3-3" : "M3 7l3-3 3 3"}
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    {dir > 0 ? "+" : ""}
                    {delta}
                    {deltaLabel && <SubText>{deltaLabel}</SubText>}
                </Delta>
            )}
        </Root>
    );
}
